import React from 'react';
import PropTypes from 'prop-types';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { QuoteNode, HeadingNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode, createLinkMatcherWithRegExp } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { MarkNode } from '@lexical/mark';
import { HashtagNode } from '@lexical/hashtag';
import { OverflowNode } from '@lexical/overflow';

import editorTheme from './theme';
import { ImageNode } from './nodes/ImageNode';
import { EmbedNode } from './nodes/EmbedNode';
import { FileLinkNode } from './nodes/FileLinkNode';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import HtmlSyncPlugin from './plugins/HtmlSyncPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';

import './editor.css';

const AUTO_LINK_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/,
    text => (text.startsWith('http') ? text : `https://${text}`)
  ),
  createLinkMatcherWithRegExp(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
    text => `mailto:${text}`
  )
];

const onEditorError = error => {
  console.error(error);
};

const Editor = ({
  initialText,
  imageFolder,
  handleChangeOut,
  minHeight = 300,
  placeholder = 'Type here…',
  maxLength
}) => {
  const initialConfig = {
    namespace: 'ActionPlanEditor',
    theme: editorTheme,
    onError: onEditorError,
    nodes: [
      QuoteNode,
      HeadingNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      CodeHighlightNode,
      HorizontalRuleNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      MarkNode,
      HashtagNode,
      OverflowNode,
      ImageNode,
      EmbedNode,
      FileLinkNode
    ]
  };
  const hasCharacterLimit = typeof maxLength === 'number';

  return (
    <div className="editor-parent" style={{ width: '100%' }}>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin imageFolder={imageFolder} />
        <div className="editor-content-wrapper" style={{ minHeight, backgroundColor: 'white' }}>
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-content-editable" />}
            placeholder={<div className="editor-placeholder">{placeholder}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <ClickableLinkPlugin />
        <TablePlugin />
        <HashtagPlugin />
        <TabIndentationPlugin />
        <HorizontalRulePlugin />
        <AutoLinkPlugin matchers={AUTO_LINK_MATCHERS} />
        <CodeHighlightPlugin />
        <HtmlSyncPlugin initialText={initialText} onHtmlChange={handleChangeOut} />
        {hasCharacterLimit && (
          <div className="editor-char-limit-wrapper">
            <CharacterLimitPlugin maxLength={maxLength} charset="UTF-16" />
          </div>
        )}
      </LexicalComposer>
    </div>
  );
};

Editor.propTypes = {
  imageFolder: PropTypes.string.isRequired,
  value: PropTypes.string,
  handleChangeOut: PropTypes.func,
  placeholder: PropTypes.string,
  initialText: PropTypes.string,
  minHeight: PropTypes.number,
  maxLength: PropTypes.number
};

export default Editor;
